import { useCallback, useEffect, useState } from 'react'

import { usePrevious } from '@lyrahealth-inc/shared-app-logic/src/features/common/hooks/usePrevious'

import { InsertMessageOptions } from '../BaseChat'

type MessageToInsert = { message: string; opts?: InsertMessageOptions }

/**
 * State and callbacks for inserting content (a message, a booking link, an Essentials article) into the
 * Messenger composer. Shared by the Messages tab and the chat popout so they stay in step.
 *
 * Anything pending belongs to the client it was inserted for, so it is dropped when switching from one
 * client directly to another. Clearing the client (opening compose) and then selecting one keeps it.
 */
export const useMessageInsertion = (selectedClientId: string | null | undefined) => {
  const [messageToInsert, setMessageToInsert] = useState<MessageToInsert | null>(null)
  const [currentTemplateId, setCurrentTemplateId] = useState<string | null | undefined>(null)
  const [shouldInsertDBL, setShouldInsertDBL] = useState(false)
  const [shouldInsertEssentialsLink, setShouldInsertEssentialsLink] = useState(false)

  const insertMessage = useCallback((message: string | null, opts?: InsertMessageOptions) => {
    if (message === null || message === '') {
      setMessageToInsert(null)
      setCurrentTemplateId(null)
      return
    }
    setMessageToInsert({ message, opts })
    setCurrentTemplateId(opts?.attributesToAttach?.message_template_id ?? null)
  }, [])
  const insertDirectBookingLink = useCallback(() => setShouldInsertDBL(true), [])
  const insertEssentialsArticleChip = useCallback(() => setShouldInsertEssentialsLink(true), [])
  const onDBLInserted = useCallback(() => setShouldInsertDBL(false), [])
  const onEssentialsLinkInserted = useCallback(() => setShouldInsertEssentialsLink(false), [])

  const prevSelectedClientId = usePrevious(selectedClientId)
  useEffect(() => {
    if (prevSelectedClientId && selectedClientId && selectedClientId !== prevSelectedClientId) {
      insertMessage(null)
      setShouldInsertDBL(false)
      setShouldInsertEssentialsLink(false)
    }
  }, [insertMessage, prevSelectedClientId, selectedClientId])

  return {
    messageToInsert,
    setMessageToInsert,
    currentTemplateId,
    shouldInsertDBL,
    shouldInsertEssentialsLink,
    insertMessage,
    insertDirectBookingLink,
    insertEssentialsArticleChip,
    onDBLInserted,
    onEssentialsLinkInserted,
  }
}
